import React, { useMemo, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { LoadingStatusWrapper } from 'sharedComponents/WithLoadingStatus';
import { ScrollableListViewComponent } from 'sharedComponents';
import GatewayEventTableHeader from './GatewayEventTableHeader';
import GatewayEventRow from './GatewayEventRow';
import './style.scss';

/**
 * Unified Gateway Event Table Component with Infinite Scroll
 * @param {Object} props - Component props
 * @param {Object} props.model - Data model containing events and loading status
 * @param {Object} props.actions - Action handlers
 * @param {string} props.eventLogType - Event log type from current tab
 */
const GatewayEventTableComponent = ({ model, actions, eventLogType }) => {
    const { t: translate } = useTranslation();

    const {
        gatewayEventsLog,
        gatewayEventLogLoadingStatus,
        isFilterExpanded,
        hasMoreData,
        isLoadingMore,
        loadMoreEvents
    } = model;

    // Calculate numeric height for ScrollableListViewComponent
    const scrollHeight = useMemo(() => {
        const viewportHeight = window.innerHeight;
        // Adjust these pixel values based on your header/filter heights
        const offset = isFilterExpanded ? 450 : 350;
        return Math.max(viewportHeight - offset, 300);
    }, [isFilterExpanded]);

    // Extract events data safely
    const events = useMemo(
        () => gatewayEventsLog?.events || [],
        [gatewayEventsLog]
    );

    // Use eventLogType prop if available, otherwise fallback to first event's eventSource
    const eventSource = useMemo(
        () => eventLogType || events?.[0]?.eventSource || '',
        [eventLogType, events]
    );

    // Render individual row
    const renderRow = useCallback(({ item }) =>
        <GatewayEventRow
            key={item.eventId}
            event={item}
            actions={actions}
            eventSource={eventSource}
        />
        , [actions, eventSource]);

    // Handle infinite scroll load more
    const handleLoadMore = useCallback(() => {
        if (hasMoreData && !isLoadingMore && loadMoreEvents) {
            loadMoreEvents();
        }
    }, [hasMoreData, isLoadingMore, loadMoreEvents]);

    return (
        <LoadingStatusWrapper loadingStatus={[gatewayEventLogLoadingStatus]}>
            <div className='gateway-event__table'>
                <GatewayEventTableHeader translate={translate} eventSource={eventSource} />
                {events.length > 0 ?
                    <ScrollableListViewComponent
                        data={events}
                        rowHeight={60}
                        height={scrollHeight}
                        width='100%'
                        renderRow={renderRow}
                        onLoadMore={handleLoadMore}
                        hasMore={hasMoreData}
                        listType='FixedSizeList'
                        emptyLabel={translate('ABB.Powertrain.Frontend.noInformationAvailableLabel')}
                        className='gateway-events-scroll-list'
                        withLoading={false}
                    />
                    :
                    <div className='gateway-event-empty'>
                        {translate('ABB.Powertrain.Frontend.noInformationAvailableLabel')}
                    </div>
                }
            </div>
        </LoadingStatusWrapper>
    );
};

GatewayEventTableComponent.propTypes = {
    model: PropTypes.shape({
        gatewayEventsLog: PropTypes.shape({
            events: PropTypes.array,
            nextCursor: PropTypes.string
        }),
        gatewayEventLogLoadingStatus: PropTypes.string,
        isFilterExpanded: PropTypes.bool,
        hasMoreData: PropTypes.bool,
        isLoadingMore: PropTypes.bool,
        loadMoreEvents: PropTypes.func
    }).isRequired,
    actions: PropTypes.shape({
        fetchGatewayEventsAttachment: PropTypes.func
    }),
    eventLogType: PropTypes.string
};

GatewayEventTableComponent.defaultProps = {
    actions: {}
};

export default React.memo(GatewayEventTableComponent);
